import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'The Psychology of Financial Stress',
    content: [
      '<strong>Learning Goal:</strong> Identify signs and causes of financial stress, understand why perceived control is central to well-being, and connect emotional regulation to sound financial decision-making.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• The financial stress cycle and how it develops',
      '• How financial anxiety manifests physically and emotionally',
      '• Understanding the 8 IFDFW dimensions in detail',
      '• The difference between objective financial status and subjective well-being',
      '<br/>',
      '<strong>Discussion:</strong> Financial stress doesn\'t just affect your wallet–it impacts your sleep, relationships, and overall quality of life. Understanding this connection is key to breaking the cycle.'
    ]
  },
  {
    title: 'The Financial Stress Cycle',
    content: [
      'Financial stress often creates a self-perpetuating cycle that can feel impossible to break without intervention.',
      '<br/>',
      '<strong>The Cycle:</strong>',
      '1. <strong>Worry</strong> about money creates anxiety',
      '2. <strong>Avoidance</strong> of financial decisions and planning',
      '3. <strong>Poor choices</strong> due to stress rather than strategy',
      '4. <strong>More stress</strong> from worsening financial situation',
      '5. <strong>Repeat</strong> the cycle with increased intensity',
      '<br/>',
      '<strong>Breaking the Cycle:</strong>',
      '• <strong>Awareness:</strong> Recognize when stress is driving decisions',
      '• <strong>Small actions:</strong> Start with one manageable financial task',
      '• <strong>Support:</strong> Talk to trusted friends or professionals',
      '• <strong>Self-compassion:</strong> Be kind to yourself during the process',
      '<br/>',
      '<strong>Key Insight:</strong> Financial stress affects decision-making capacity, often leading to choices that create more stress rather than less.'
    ]
  },
  {
    title: 'How Financial Anxiety Manifests',
    content: [
      'Financial stress doesn\'t stay contained in your mind–it affects your body, emotions, and behavior in significant ways.',
      '<br/>',
      '<strong>Physical Symptoms:</strong>',
      '• <strong>Sleep issues:</strong> Difficulty falling asleep or staying asleep',
      '• <strong>Headaches:</strong> Tension headaches from constant worry',
      '• <strong>Digestive problems:</strong> Stomach issues from stress',
      '• <strong>Fatigue:</strong> Mental and physical exhaustion',
      '• <strong>Reduced immunity:</strong> More frequent illness',
      '<br/>',
      '<strong>Emotional & Behavioral Symptoms:</strong>',
      '• <strong>Irritability:</strong> Short temper with loved ones',
      '• <strong>Decision fatigue:</strong> Difficulty making any choices',
      '• <strong>Avoidance:</strong> Ignoring bills or financial matters',
      '• <strong>Impulsive spending:</strong> Emotional spending to feel better temporarily',
      '• <strong>Social withdrawal:</strong> Avoiding social activities due to shame',
      '<br/>',
      '<strong>Why This Matters:</strong> Recognizing these symptoms helps you address financial stress before it affects other areas of your wellness.'
    ]
  },
  {
    title: 'Understanding the 8 IFDFW Dimensions',
    content: [
      'Each IFDFW dimension represents a different aspect of financial well-being, helping you identify your specific areas of strength and concern.',
      '<br/>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">DIMENSION</div>
          <div class="table-header means-cell">WHAT IT MEASURES</div>
          <div class="table-header matters-cell">EXAMPLE STATEMENT</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">1. Worry about expenses</div>
          <div class="table-cell means-cell">Current financial anxiety</div>
          <div class="table-cell matters-cell">"I worry about meeting monthly expenses"</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">2. Emergency confidence</div>
          <div class="table-cell means-cell">Preparedness for emergencies</div>
          <div class="table-cell matters-cell">"I could handle an unexpected $1,000 expense"</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">3. Paycheck-to-paycheck</div>
          <div class="table-cell means-cell">Income stability</div>
          <div class="table-cell matters-cell">"I live paycheck to paycheck"</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">4. Current satisfaction</div>
          <div class="table-cell means-cell">Present financial contentment</div>
          <div class="table-cell matters-cell">"I am satisfied with my current finances"</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">5. Sense of control</div>
          <div class="table-cell means-cell">Financial decision confidence</div>
          <div class="table-cell matters-cell">"I feel in control of my finances"</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">6. Future worry</div>
          <div class="table-cell means-cell">Future financial security</div>
          <div class="table-cell matters-cell">"I worry about my financial future"</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">7. Overall stress</div>
          <div class="table-cell means-cell">General financial anxiety</div>
          <div class="table-cell matters-cell">"My finances cause me a lot of stress"</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">8. Overall well-being</div>
          <div class="table-cell means-cell">General financial satisfaction</div>
          <div class="table-cell matters-cell">"I have a positive financial outlook"</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Key Insight:</strong> The IFDFW scale helps you pinpoint exactly where your financial stress originates, making it easier to address specific concerns.'
    ]
  },
  {
    title: 'Why Perceived Control Matters',
    content: [
      'Research shows that perceived control over your finances is often more important than your actual financial situation for overall well-being.',
      '<br/>',
      '<strong>Objective vs. Subjective Financial Well-Being:</strong>',
      '• <strong>Objective:</strong> Measurable facts (income, savings, debt)',
      '• <strong>Subjective:</strong> How you feel about your financial situation',
      '<br/>',
      '<strong>Why Perception Matters:</strong>',
      '• People with similar financial situations can have very different stress levels',
      '• Feeling in control reduces anxiety and improves decision-making',
      '• Perceived control motivates positive financial behaviors',
      '• Lack of perceived control can lead to avoidance and poor choices',
      '<br/>',
      '<strong>Building Perceived Control:</strong>',
      '• <strong>Knowledge:</strong> Learn basic financial concepts',
      '• <strong>Planning:</strong> Create simple financial plans',
      '• <strong>Tracking:</strong> Monitor your financial progress',
      '• <strong>Small wins:</strong> Celebrate financial achievements',
      '<br/>',
      '<strong>Takeaway:</strong> Financial well-being is as much about mindset and perceived control as it is about actual financial status.'
    ],
    additionalSection: {
      title: 'Stress Mapping Exercise',
      content: [
        'Take the IFDFW assessment and create a personal stress map:',
        '<br/>',
        '<strong>Rate yourself (1-10) on each dimension:</strong>',
        '1. Worry about expenses: ___',
        '2. Confidence in emergencies: ___',
        '3. Paycheck-to-paycheck living: ___',
        '4. Current financial satisfaction: ___',
        '5. Sense of financial control: ___',
        '6. Worry about future finances: ___',
        '7. Overall financial stress: ___',
        '8. Overall financial well-being: ___',
        '<br/>',
        '<strong>Analysis Questions:</strong>',
        '• Which area causes the most strain?',
        '• Which area feels strongest?',
        '• What patterns do you notice?',
        '• How does this align with your actual financial situation?',
        '<br/>',
        '<strong>Reflection:</strong> This exercise helps identify your specific financial stress triggers and areas of strength.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 2: The Psychology of Financial Stress! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about financial stress and the IFDFW dimensions.',
        'The quiz will help you review key concepts about money psychology and stress management.'
      ]
    }
  }
];

const FinancialModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('financialM2');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/financial-module-2/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/financial-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left financial-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">The Psychology of Financial Stress</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Identify signs and causes of financial stress, understand why perceived control is central to well-being, and connect emotional regulation to sound financial decision-making.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/financial-module-2/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule2Page;

